<script setup lang="ts">
import { HotelFiltersPanel } from '@apps/main/new-desktop/modules/hotelFilters';
import { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import type { FlightsFilterEvents } from '@apps/main/new-desktop/modules/flightsFilters';
import type { FerryFilterEvents } from '@apps/main/new-desktop/modules/ferryFilters';
import { FlightsFiltersPanel } from '@apps/main/new-desktop/modules/flightsFilters';
import { FerryFiltersPanel } from '@apps/main/new-desktop/modules/ferryFilters';
import { HotelDetailsPanel } from '@apps/main/new-desktop/modules/hotelDetails';
import { ActivityDetailsPanel } from '@apps/main/new-desktop/modules/activityDetails';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import {
  useEditingIngredientStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';

import {
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
} from '../stores';
import { TripOverviewPanelType } from '../models';

import TripPriceSummaryPanel from './panels/TripPriceSummaryPanel.vue';
import {
  ConfigurationPanel,
  ConfigurationStepperPanel,
} from './configurationPanel';

const destinationSelectorStore = useDestinationSelectorStore();
const editTripStore = useEditTripStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const editingIngredientStore = useEditingIngredientStore();
const flightsFiltersStore = useFlightsFiltersStore();
const ferryFiltersStore = useFerryFiltersStore();
const statsigEventsStore = useStatsigEventsStore();

function closeConfigurationPanel(): void {
  tripOverviewPanelsStore.closeConfigurationPanel();
  tripOverviewModalsStore.openInitialFerryModalWhenPossible();
}

function onCloseFlightsFiltersPanel(): void {
  tripOverviewPanelsStore.closeFlightsFiltersPanel();
  flightsFiltersStore.clearAllowedFlightIngredientIds();
}

function onApplyAllFlightsFilters(filters: FlightsFilterEvents[]): void {
  flightsFiltersStore.updateAllFilters(filters);
  tripOverviewPanelsStore.closeFlightsFiltersPanel();
  flightsFiltersStore.clearAllowedFlightIngredientIds();
}

function onCloseFerryFiltersPanel(): void {
  tripOverviewPanelsStore.closeFerryFiltersPanel();
  ferryFiltersStore.clearAllowedFerryIngredientIds();
}

function onApplyAllFerryFilters(filters: FerryFilterEvents[]): void {
  ferryFiltersStore.updateAll(filters);
  tripOverviewPanelsStore.closeFerryFiltersPanel();
  ferryFiltersStore.clearAllowedFerryIngredientIds();
}

function onApplyHotelFilters(): void {
  tripOverviewPanelsStore.applyHotelFilters();
  statsigEventsStore.logTripOverviewFiltersApplied();
}

function onResetHotelFilters(): void {
  tripOverviewPanelsStore.resetHotelFilters();
  statsigEventsStore.logTripOverviewFiltersReset();
}

function handleChangeNumberOfNights(segment: EditTripSegment): void {
  editTripStore.changeNumberOfNights(
    segment.ingredientId,
    segment.numberOfNights
  );
}
</script>

<template>
  <ActivityDetailsPanel
    v-if="
      tripOverviewPanelsStore.tripOverviewPanelType ===
        TripOverviewPanelType.ActivityDetails &&
      tripOverviewPanelsStore.activityDetailsData
    "
    :activity-details="tripOverviewPanelsStore.activityDetailsData"
    @close="tripOverviewPanelsStore.closeActivityDetailsPanel()"
  />

  <ConfigurationPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.Configuration
    "
    @close="closeConfigurationPanel"
    @confirm="closeConfigurationPanel"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="handleChangeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
    @update-pax="tripOverviewPanelsStore.updatePax"
  />

  <ConfigurationStepperPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.ConfigurationStepper
    "
    @close="tripOverviewPanelsStore.closeConfigurationStepperPanel"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="handleChangeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
    @update-pax="tripOverviewPanelsStore.updatePax"
  />

  <HotelDetailsPanel
    v-if="
      tripOverviewPanelsStore.tripOverviewPanelType ===
        TripOverviewPanelType.HotelDetails &&
      tripOverviewPanelsStore.hotelDetailsStaticData &&
      tripOverviewPanelsStore.hotelDetailsDynamicData
    "
    :hotel-static-data="tripOverviewPanelsStore.hotelDetailsStaticData"
    :hotel-dynamic-data="tripOverviewPanelsStore.hotelDetailsDynamicData"
    :hotel-meal-filter-options="tripOverviewPanelsStore.hotelMealFilterOptions"
    :source="HotelDetailsEventSource.TripOverview"
    @close="tripOverviewPanelsStore.closeHotelDetailsPanel()"
    @stay-selected="tripOverviewPanelsStore.selectStay"
    @toggle-heart="tripOverviewPanelsStore.toggleHeart"
    @meal-option-selected="tripOverviewPanelsStore.updateMealFilter"
  />

  <TripPriceSummaryPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.TripPriceSummary
    "
    @close="tripOverviewPanelsStore.closeTripPriceSummaryPanel"
  />

  <HotelFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.HotelFilters
    "
    :disable-hotel-name-filter="true"
    :ingredient="editingIngredientStore.editingStayIngredient"
    @close="tripOverviewPanelsStore.closeHotelFiltersPanel()"
    @update-filters="tripOverviewPanelsStore.updateHotelFilters($event)"
    @apply-filters="onApplyHotelFilters"
    @reset-filters="onResetHotelFilters"
  />

  <FlightsFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.FlightsFilters
    "
    :header-text="$t('filter.header')"
    :data="flightsFiltersStore.filters"
    :should-update-on-click="true"
    @apply-all-filters="onApplyAllFlightsFilters"
    @close="onCloseFlightsFiltersPanel"
    @reset-all-filters="flightsFiltersStore.resetFlightFilters"
  />

  <FerryFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.FerryFilters
    "
    :data="ferryFiltersStore.filters"
    :should-update-on-click="true"
    :header-text="$t('filter.header')"
    @close="onCloseFerryFiltersPanel"
    @apply-all-filters="onApplyAllFerryFilters"
    @reset-all-filters="ferryFiltersStore.resetAllFilters"
  />
</template>
